/**
 * Tagesabschluss-Assistent: reine Rechenlogik für den geführten Kassenabschluss
 * (offene Tische → Kassasturz mit Zählbrett → Terminal-Tagesabschluss →
 * Tagesbericht). Kein Beleg entsteht hier; der Monatsbeleg kommt vom
 * Pflichtbeleg-Lauf des Servers.
 */

/** Nennwerte der Euro-Scheine und -Münzen in Cent, größte zuerst (Regel 5). */
export const NENNWERTE_CENT: readonly number[] = [
  50_000, 20_000, 10_000, 5_000, 2_000, 1_000, 500, 200, 100, 50, 20, 10, 5, 2, 1,
];

/** Anzahl je Nennwert (Schlüssel: Nennwert in Cent). */
export type Zaehlbrett = Readonly<Record<number, number>>;

/** Summe des Zählbretts in Cent; unbrauchbare Einträge (negativ, nicht ganzzahlig, unbekannter Nennwert) zählen nicht. */
export function zaehlsumme(brett: Zaehlbrett): number {
  let summe = 0;
  for (const wert of NENNWERTE_CENT) {
    const anzahl = brett[wert];
    if (anzahl !== undefined && Number.isInteger(anzahl) && anzahl > 0) {
      summe += wert * anzahl;
    }
  }
  return summe;
}

/** Anzahl aus einem Eingabefeld: leer oder unbrauchbar → 0. */
export function parseAnzahl(text: string): number {
  const t = text.trim();
  if (!/^\d{1,4}$/.test(t)) {
    return 0;
  }
  return Number(t);
}

export type SchrittStatus = 'offen' | 'erledigt' | 'uebersprungen' | 'warnung';

export interface Schritt {
  id: 'tische' | 'zaehlung' | 'terminal' | 'bericht' | 'protokoll';
  titel: string;
  status: SchrittStatus;
  text: string;
}

export interface AbschlussLage {
  /** Offene Tische (Server und lokal) – sollten vor dem Abschluss kassiert sein. */
  offeneTische: number;
  /** Kassasturz heute gebucht (Zeitpunkt), sonst null. */
  zaehlungHeute: string | null;
  /** Terminal-Tagesabschluss nötig (ZVT-Terminal mit Bridge)? */
  terminalNoetig: boolean;
  /** Ergebnis des Terminal-Tagesabschlusses in dieser Sitzung. */
  terminalStatus: 'offen' | 'uebernommen' | 'erledigt' | 'fehlgeschlagen' | null;
  /** Tagesbericht geladen und gedruckt/ausgegeben? */
  berichtAusgegeben: boolean;
  /** Am Server protokollierter Abschluss des Tages (Uhrzeit), sonst null. */
  protokolliert: string | null;
  /** Nach dem protokollierten Abschluss wurde noch kassiert? */
  protokollVeraltet: boolean;
}

/** Schrittliste des Assistenten mit Status und Erklärung aus der Lage ableiten. */
export function abschlussSchritte(lage: AbschlussLage): Schritt[] {
  const tische: Schritt = {
    id: 'tische',
    titel: 'Offene Tische',
    ...(lage.offeneTische > 0
      ? {
          status: 'warnung',
          text: `${String(lage.offeneTische)} offene(r) Bon(s) – vor dem Abschluss kassieren oder verwerfen`,
        }
      : { status: 'erledigt', text: 'Keine offenen Bons' }),
  };
  const zaehlung: Schritt = {
    id: 'zaehlung',
    titel: 'Kassasturz',
    ...(lage.zaehlungHeute === null
      ? { status: 'offen', text: 'Bargeld zählen und als Kassasturz buchen' }
      : { status: 'erledigt', text: `Gezählt um ${lage.zaehlungHeute}` }),
  };
  let terminal: Schritt;
  if (!lage.terminalNoetig) {
    terminal = {
      id: 'terminal',
      titel: 'Terminal-Tagesabschluss',
      status: 'uebersprungen',
      text: 'Kein ZVT-Terminal mit Bridge an dieser Kasse',
    };
  } else if (lage.terminalStatus === 'erledigt') {
    terminal = {
      id: 'terminal',
      titel: 'Terminal-Tagesabschluss',
      status: 'erledigt',
      text: 'Kassenschnitt am Terminal durchgeführt',
    };
  } else if (lage.terminalStatus === 'fehlgeschlagen') {
    terminal = {
      id: 'terminal',
      titel: 'Terminal-Tagesabschluss',
      status: 'warnung',
      text: 'Kassenschnitt fehlgeschlagen – am Terminal prüfen und wiederholen',
    };
  } else {
    terminal = {
      id: 'terminal',
      titel: 'Terminal-Tagesabschluss',
      status: 'offen',
      text:
        lage.terminalStatus === null
          ? 'Kassenschnitt (Tagesabschluss) am Kartenterminal auslösen'
          : 'Läuft – die Bridge führt den Kassenschnitt aus',
    };
  }
  const bericht: Schritt = {
    id: 'bericht',
    titel: 'Tagesbericht',
    ...(lage.berichtAusgegeben
      ? { status: 'erledigt', text: 'Tagesbericht ausgegeben' }
      : { status: 'offen', text: 'Tagesbericht prüfen und drucken (Bon oder Browser)' }),
  };
  let protokoll: Schritt;
  if (lage.protokolliert === null) {
    protokoll = {
      id: 'protokoll',
      titel: 'Abschluss protokollieren',
      status: 'offen',
      text: 'Abschluss mit Tagesbericht am Server festhalten',
    };
  } else if (lage.protokollVeraltet) {
    protokoll = {
      id: 'protokoll',
      titel: 'Abschluss protokollieren',
      status: 'warnung',
      text: `Protokolliert um ${lage.protokolliert}, danach noch kassiert – Nachtrag protokollieren`,
    };
  } else {
    protokoll = {
      id: 'protokoll',
      titel: 'Abschluss protokollieren',
      status: 'erledigt',
      text: `Protokolliert um ${lage.protokolliert}`,
    };
  }
  return [tische, zaehlung, terminal, bericht, protokoll];
}

/** Abschluss gilt als vollständig, wenn kein Schritt mehr offen ist oder warnt. */
export function abschlussVollstaendig(schritte: readonly Schritt[]): boolean {
  return schritte.every((s) => s.status === 'erledigt' || s.status === 'uebersprungen');
}

/** Letzter Tag des Monats (Wiener Datum `JJJJ-MM-TT`)? Dann steht der Monatsbeleg an. */
export function istMonatsletzter(datum: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum);
  if (m === null) {
    return false;
  }
  const jahr = Number(m[1]);
  const monat = Number(m[2]);
  const tag = Number(m[3]);
  // Tag 0 des Folgemonats = letzter Tag dieses Monats (UTC, nur Kalenderrechnung)
  const letzter = new Date(Date.UTC(jahr, monat, 0)).getUTCDate();
  return tag === letzter;
}

/** Kennzeichen der Trinkgeld-Entnahme im Kassenbuch (Grund beginnt damit). */
export const TRINKGELD_GRUND = 'Trinkgeld Karte an Bediener';

/**
 * Noch auszuzahlendes Kartentrinkgeld des Tages: Trinkgeld laut Tagesbericht minus
 * bereits gebuchter Trinkgeld-Entnahmen – ein zweiter Klick zahlt nicht doppelt aus.
 */
export function trinkgeldOffen(
  trinkgeldCent: number,
  eintraegeHeute: readonly { art: string; betragCent: number; grund: string | null }[],
): number {
  const ausgezahlt = eintraegeHeute
    .filter((e) => e.art === 'entnahme' && (e.grund ?? '').startsWith(TRINKGELD_GRUND))
    .reduce((s, e) => s + e.betragCent, 0);
  return Math.max(0, trinkgeldCent - ausgezahlt);
}
